/*
 * © 2023 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ServerError = exports.UnauthorisedError = exports.TimeoutError = exports.BadRequestError = exports.NotImplementedError = exports.TooManyRequestsError = void 0;
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name TooManyRequestsError
 * @description The request rate limit has been exceeded. Wait a few minutes, then try again.
 * @summary Service temporarily throttled
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TooManyRequestsError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Service temporarily throttled',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-0001',
            status: 429,
            errorCode: 'SNYK-0001',
            level: 'warn',
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.TooManyRequestsError = TooManyRequestsError;
/**
 * @class
 * @name NotImplementedError
 * @description The server doesn’t recognize the request method, or it cannot fulfill it. Review the request and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-api-info](https://docs.snyk.io/snyk-api-info)
 * @summary Server error response
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class NotImplementedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Server error response',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-0002',
            status: 501,
            errorCode: 'SNYK-0002',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-api-info',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.NotImplementedError = NotImplementedError;
/**
 * @class
 * @name BadRequestError
 * @description The server cannot process the request due to a client error, such as malformed request syntax, size too large, invalid request message framing, or deceptive request routing. Review the request and try again.
 * @summary Client request cannot be processed
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class BadRequestError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Client request cannot be processed',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-0003',
            status: 400,
            errorCode: 'SNYK-0003',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.BadRequestError = BadRequestError;
/**
 * @class
 * @name TimeoutError
 * @description The server timed out during the request. Check Snyk status, then try again.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary Server communication error
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TimeoutError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Server communication error',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-0004',
            status: 504,
            errorCode: 'SNYK-0004',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.TimeoutError = TimeoutError;
/**
 * @class
 * @name UnauthorisedError
 * @description Authentication credentials not recognized, or user access is not provisioned. Revise credentials and try again, or request access from your Snyk administrator.
 * @summary Authentication error
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class UnauthorisedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Authentication error',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-0005',
            status: 401,
            errorCode: 'SNYK-0005',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.UnauthorisedError = UnauthorisedError;
/**
 * @class
 * @name ServerError
 * @description The server cannot process the request due to an unexpected error. Check Snyk status, then try again.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary Request not fulfilled due to server error
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ServerError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance) {
        super({
            title: 'Request not fulfilled due to server error ',
            type: 'https://docs.snyk.io/more-info/error-catalog#snyk-9999',
            status: 500,
            errorCode: 'SNYK-9999',
            level: 'error',
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
            ] }, additionalData), cause);
        this.name = this.constructor.name;
    }
}
exports.ServerError = ServerError;
//# sourceMappingURL=Snyk-error-catalog.js.map